/**
 * Judgements about a certification report that the pages share.
 *
 * Kept out of the pages so the reasoning lives in one place and can be tested
 * without rendering anything.
 */

/**
 * Case outcomes that mean the participant has not done anything yet.
 *
 * BLOCKED belongs here: a case blocked on Sandbox infrastructure — ISO-SEC-001
 * waits on mTLS — reports that way in every run, including a brand-new empty
 * one, so treating it as activity would make any run look exercised.
 */
export const UNTOUCHED = ['NOT_RUN', 'BLOCKED', 'SKIPPED'];

/**
 * Whether anything in this report was actually scored.
 *
 * It answers two questions the pages ask: whether to show the run in progress
 * or fall back to the last issued report, and whether there is anything to
 * finalize.
 *
 * @param {Object|null} report - a certification report, live or finalized
 * @returns {boolean} true when at least one case has an outcome of its own
 */
export const hasBeenExercised = (report) =>
  (report?.cases || []).some(c => c.status && !UNTOUCHED.includes(c.status));

/** The decisions that issue a certificate. */
export const APPROVALS = ['APPROVED', 'APPROVED_WITH_CONDITIONS'];

const reportIdentity = (report) => ({
  reportId: report?.reportId,
  resultHash: report?.integrity?.resultHash,
});

/**
 * The sign-off request for a report. It names the report the officer reviewed,
 * by id and result hash, so the engine refuses it if a newer report has been
 * finalized since or this one has already been decided.
 */
export const signOffBody = (report, { decision, authorisingOfficer, title, notes }) => ({
  ...reportIdentity(report), decision, authorisingOfficer, title, notes,
});

/** The request revoking the certificate issued from a report. The reason is required. */
export const revokeBody = (report, { reason, authorisingOfficer, title }) => ({
  ...reportIdentity(report), reason, authorisingOfficer, title,
});

/**
 * What the officer may still do with a report. Decisions are final: an
 * undecided report can be decided, an approval can only be revoked, and a
 * rejection or a revocation stands (the participant finalizes a new run).
 *
 * @returns {{stage: 'NONE'|'UNDECIDED'|'APPROVED'|'REJECTED'|'REVOKED', canDecide: boolean, canRevoke: boolean}}
 */
export const decisionStage = (report) => {
  if (!report) return { stage: 'NONE', canDecide: false, canRevoke: false };
  const decision = report.signOff?.decision;
  if (!decision) return { stage: 'UNDECIDED', canDecide: true, canRevoke: false };
  if (APPROVALS.includes(decision)) return { stage: 'APPROVED', canDecide: false, canRevoke: true };
  return { stage: decision === 'REVOKED' ? 'REVOKED' : 'REJECTED', canDecide: false, canRevoke: false };
};

/** A time from the engine: an ISO string, or epoch seconds if the engine writes numbers. */
const isoTime = (t) => (typeof t === 'number' ? new Date(Math.round(t * 1000)).toISOString() : t);

/**
 * The rows of a report's decision history, oldest first.
 *
 * A report decided before the history was kept has its decision on the report
 * and no history entries; that decision is shown as the one row, marked
 * `beforeHistory` so the page can say so.
 *
 * @param {Object|null} report - the report, carrying its current sign-off
 * @param {Array|undefined} entries - the engine's history entries for it
 */
export const decisionHistory = (report, entries) => {
  if (entries?.length) {
    return entries.map(e => ({
      action: e.action, officer: e.officer, title: e.title ?? null, note: e.note ?? null,
      decidedAt: isoTime(e.decidedAt), beforeHistory: false,
    }));
  }
  const so = report?.signOff;
  if (!so?.decision) return [];
  return [{
    action: so.decision, officer: so.authorisingOfficer, title: so.title ?? null,
    note: so.conditions?.[0]?.condition ?? null, decidedAt: so.decisionAt, beforeHistory: true,
  }];
};

/** A decision as a participant or officer reads it: "Approved with conditions", "Certificate revoked". */
export const decisionLabel = (decision) => {
  if (!decision) return '';
  if (decision === 'REVOKED') return 'Certificate revoked';
  const words = decision.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const LISTED = 5;
const listIds = (ids) => (ids.length <= LISTED ? ids.join(', ')
  : `${ids.slice(0, LISTED).join(', ')} and ${ids.length - LISTED} more`);
const mandatoryCases = (report) => (report?.cases || []).filter((c) => c.obligation === 'MANDATORY');
const idsWith = (cases, ...statuses) => cases.filter((c) => statuses.includes(c.status)).map((c) => c.caseId);

/**
 * Why this run may not be finalized yet, or null when it may: the engines'
 * rule (CertificationFinalizeRules), in the same words, so the Finalize button
 * can say why it is disabled. The engine still decides.
 *
 * Only a run that could be approved may be finalized: no mandatory case failed
 * or left to run, and at least one passed. BLOCKED cases are not held against it.
 */
export const finalizeBlocker = (report) => {
  if (!report) return 'There is no run to finalize yet.';
  const mandatory = mandatoryCases(report);
  const failed = idsWith(mandatory, 'FAIL');
  if (failed.length) {
    const one = failed.length === 1;
    return `${failed.length} mandatory case${one ? '' : 's'} failed: ${listIds(failed)}. Fix ${one ? 'it' : 'them'} `
      + `and run ${one ? 'it' : 'them'} again; the latest outcome of each case is what counts.`;
  }
  const notRun = idsWith(mandatory, 'NOT_RUN', 'SKIPPED');
  if (notRun.length) {
    const one = notRun.length === 1;
    return `${notRun.length} of ${mandatory.length} mandatory case${mandatory.length === 1 ? '' : 's'} `
      + `${one ? 'has' : 'have'} not been run yet: ${listIds(notRun)}. Run ${one ? 'it' : 'them'}, then finalize.`;
  }
  if (!idsWith(mandatory, 'PASS').length) return 'No mandatory case has passed yet, so there is nothing to certify.';
  if (!['PASS', 'CONDITIONAL_PASS'].includes(report.status)) {
    return `This run's verdict is ${report.status ? report.status.replace(/_/g, ' ') : 'not known yet'}; `
      + 'only a passing run can be finalized.';
  }
  return null;
};

/** The verdict being signed, in one line, for the Finalize confirmation. */
export const verdictSummary = (report) => {
  const mandatory = mandatoryCases(report);
  const passed = idsWith(mandatory, 'PASS').length;
  const blocked = idsWith(mandatory, 'BLOCKED').length;
  const optionalFailed = (report?.cases || []).filter((c) => c.obligation === 'OPTIONAL' && c.status === 'FAIL').length;
  return `${(report?.status || 'NOT RUN').replace(/_/g, ' ')}: ${passed} of ${mandatory.length} mandatory `
    + `case${mandatory.length === 1 ? '' : 's'} passed`
    + (blocked ? ` (${blocked} blocked by the Sandbox)` : '')
    + (optionalFailed ? `, ${optionalFailed} optional case${optionalFailed === 1 ? '' : 's'} failed` : '');
};

/** Whether the Sandbox's automated run drove this report's run: it cannot be approved. */
export const isAutomatedRun = (report) => report?.runWindow?.mode === 'AUTOMATED';

/** Who drove the run, as the report states it. */
export const runModeLabel = (report) => {
  const mode = report?.runWindow?.mode;
  if (mode === 'AUTOMATED') return 'Automated by the Sandbox';
  if (mode === 'PARTICIPANT') return "The participant's own run";
  return 'Not recorded on reports issued before run modes were';
};

const sha256Hex = async (text) => {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

// The report builder's placeholder: sha256 of "cert:<FSP>:<TENANT>", each id
// upper-cased with anything but letters and digits removed.
const placeholderFingerprint = async (report) => {
  const safe = (s, dflt) => (!s || !s.trim() ? dflt : s.toUpperCase().replace(/[^A-Z0-9]/g, ''));
  return `sha256:${await sha256Hex(`cert:${safe(report?.tenant?.fspId, 'FSP')}:${safe(report?.tenant?.tenantId, 't')}`)}`;
};

/**
 * The participant credential a report names, and what to say about it. Reports
 * issued before runs pinned the real one carry a placeholder, which cannot change
 * without breaking their hash; it is shown as it is, and said to be one.
 *
 * @returns {Promise<{value: string|null, note: string|null}>}
 */
export const credentialLabel = async (report) => {
  const value = report?.environment?.tenantCertFingerprint || null;
  if (!value) return { value: null, note: null };
  if (value === 'none') return { value, note: 'No credential registered' };
  if (value === await placeholderFingerprint(report)) {
    return { value, note: 'Placeholder: this report was issued before credential fingerprints were recorded' };
  }
  return { value, note: null };
};
